import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

const DropZoneInput = styled(InputBase)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  borderColor: theme.palette.grey[300],
  borderWidth: 1,
  boxShadow: theme.insets[2],
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.09)',
  },
  '&.Mui-readOnly': {
    cursor: 'default',
  },
}));

function DropZone({ disabled, error, helper, label, onChange, readOnly }) {
  const [file, setFile] = useState(null);
  const onDrop = useCallback((acceptedFiles) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      onChange(e.target.result);
      setFile(e.target.result);
    };
    fileReader.readAsDataURL(acceptedFiles[0]);
  }, [onChange, setFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: readOnly || disabled,
    maxFiles: 1,
    maxSize: 1024 * 1024 * 10, // 10 MB
    onDrop,
  });

  return (
    <FormControl
      variant='filled'
      error={error}
      fullWidth
    >
      <InputLabel shrink>{label}</InputLabel>
      <DropZoneInput
        slots={{
          input: () => (
            <>
              <input {...getInputProps()} />

              <Stack
                direction='column'
                gap={1}
                alignItems='center'
                sx={{ width: '100%' }}
              >
                {
                  isDragActive ? (
                    <Typography variant='body1' color='grey.800' align='center'>
                      Drop file here to upload
                    </Typography>
                  ) : (
                    <Typography variant='body1' color='grey.800' align='center'>
                      Drop file here or click to upload.<br />
                    </Typography>
                  )
                }
                {
                  file && (
                    <a href={file} target="_blank" rel="noreferrer">
                      <img src={file} alt="" />
                    </a>
                  )
                }
              </Stack>
            </>
          )
        }}
        disabled={disabled}
        readOnly={readOnly}
        {...getRootProps()}
        fullWidth
      >
      </DropZoneInput>
      <FormHelperText>{helper || 'Accept file upload within 10 MB'}</FormHelperText>
    </FormControl>
  );
}

DropZone.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.string,
  helper: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default DropZone;
